import * as React from "react";
import {
  ChamberMemberReturn,
  GetAllChamberMembersQuery,
  GetAllRewardCampaignsForChamberQuery,
  PrizeDrawParticipantReturn,
  RewardCampaignReturn,
} from "../../../graphql/types";
import { getLocalTimestamp } from "../Root/RootAdmin";
import { Button, makeStyles, Modal, IconButton, useMediaQuery, Menu, MenuItem, TextField, InputAdornment } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import theme from "../../../themes";
import { EndCampaignModal } from "./updateChamberRewardCampaign";
import LogoImage from "../../common/utilities/LogoImage";
import { checkIfRewardCampaignIsActive } from "../../common/utilities/rewardCampaign";
import ParticipantTable from "./ParticipantTable";
import HoverToolTip from "../../common/utilities/HoverToolTip";
import CommonTable, { HeadCell } from "../../common/CommonTable";
import CloseIcon from "@material-ui/icons/Close";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import GetAppIcon from "@material-ui/icons/GetApp";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import SearchIcon from "@material-ui/icons/Search";

type OmittedRedeemSettingsRewardCampaign = RewardCampaignReturn & {
  chamberMemberInfo: ChamberMemberReturn;
};
type AllRewardCampaignDataType =
  | GetAllRewardCampaignsForChamberQuery["getAllRewardCampaignsForChamber"]
  | undefined
  | null;
type TableRewardCampaignDataType = OmittedRedeemSettingsRewardCampaign;
type AllTableRewardCampaignDataType = TableRewardCampaignDataType[];


const useStyles = makeStyles(() => ({
  modal: {
    border: "unset",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    borderRadius: "16px",
    textAlign: "center",
    width: "740px",
    maxWidth: "90%",
    maxHeight: "90vh",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: "40px 40px 30px",
    position: "relative",
    outline: "none",
  },
  winnerPaper: {
    borderRadius: "16px",
    textAlign: "center",
    width: "500px",
    maxWidth: "90%",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    padding: "40px 40px 30px",
    position: "relative",
    outline: "none",
  },
  closeButton: {
    position: "absolute",
    top: "12px",
    right: "12px",
    backgroundColor: "#ED1C24",
    color: "#FFFFFF",
    width: "30px",
    height: "30px",
    padding: 0,
    "&:hover": {
      backgroundColor: "#D41920",
    },
  },
  viewUsersButton: {
    backgroundColor: "#00B621",
    color: "#FFFFFF",
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 500,
    fontSize: "12px",
    borderRadius: "10px",
    textTransform: "uppercase" as const,
    padding: "4px 0px",
    lineHeight: "15px",
    width: "123px",
    height: "38px",
    "&:hover": {
      backgroundColor: "#009A1B",
    },
  },
  viewUsersButtonInactive: {
    backgroundColor: "#37474f",
    color: "#FFFFFF",
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 500,
    fontSize: "12px",
    borderRadius: "10px",
    textTransform: "uppercase" as const,
    padding: "4px 0px",
    lineHeight: "15px",
    width: "123px",
    height: "38px",
    "&:hover": {
      backgroundColor: "#263238",
    },
  },
  updateButton: {
    backgroundColor: "#E1A731",
    color: "#FFFFFF",
    fontFamily: "Montserrat, sans-serif",
    fontWeight: 500,
    fontSize: "12px",
    borderRadius: "22px",
    textTransform: "uppercase" as const,
    padding: "8px 12px",
    width: "66px",
    height: "29px",
    lineHeight: "15px",
    "&:hover": {
      backgroundColor: "#C8922A",
    },
  },
  // ── Mobile list view ──────────────────────────────────────────────
  mobileList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "12px",
    width: "100%",
  },
  mobileSearchBar: {
    width: "100%",
    marginBottom: "4px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#FFFFFF",
      "& fieldset": { borderColor: "#E0E0E0" },
      "&.Mui-focused fieldset": { borderColor: "#000", borderWidth: "1.5px" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 10px",
      fontSize: "14px",
    },
  },
  mobileCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: "14px",
    padding: "14px",
    color: "#24272C",
    fontFamily: "DM Sans, sans-serif",
    display: "flex",
    flexDirection: "column" as const,
    gap: "10px",
    border: "1px solid #E8E8E8",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  mobileCardTop: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  mobileAvatar: {
    width: "40px",
    height: "40px",
    minWidth: "40px",
    borderRadius: "50%",
    backgroundColor: "#E1A731",
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "12px",
    letterSpacing: "0.5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  mobileTitles: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column" as const,
    gap: "2px",
  },
  mobileSponsorName: {
    fontWeight: 700,
    fontSize: "15px",
    color: "#24272C",
    lineHeight: 1.25,
    wordBreak: "break-word" as const,
  },
  mobileCampaignName: {
    fontWeight: 400,
    fontSize: "13px",
    color: "#666",
    lineHeight: 1.3,
    wordBreak: "break-word" as const,
  },
  mobileStatusPill: {
    display: "inline-block",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase" as const,
    letterSpacing: "0.3px",
    color: "#888",
    whiteSpace: "nowrap" as const,
    alignSelf: "flex-start" as const,
    marginTop: "2px",
  },
  mobileStatusPillActive: {
    color: "#2E7D32",
  },
  mobileStatusPillUpcoming: {
    color: "#E1A731",
  },
  mobileMenuBtn: {
    color: "#666",
    padding: "2px",
    marginLeft: "2px",
  },
  mobileDivider: {
    height: "1px",
    backgroundColor: "#EEE",
    width: "100%",
  },
  mobileMetaRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "10px",
    fontSize: "13px",
  },
  mobileMetaLabel: {
    color: "#666",
    fontWeight: 500,
  },
  mobileMetaValue: {
    color: "#24272C",
    fontWeight: 600,
    textAlign: "right" as const,
    wordBreak: "break-word" as const,
  },
  mobileBottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },
  mobileCountNumber: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#24272C",
    lineHeight: 1,
  },
  mobileCountLabel: {
    fontSize: "12px",
    color: "#666",
    marginTop: "2px",
  },
  mobileViewUsersBtn: {
    backgroundColor: "#E1A731",
    color: "#FFFFFF",
    border: "none",
    borderRadius: "10px",
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 700,
    textTransform: "none" as const,
    whiteSpace: "nowrap" as const,
    "&:hover": {
      backgroundColor: "#C8922A",
    },
  },
  mobileEmpty: {
    color: "#666",
    textAlign: "center" as const,
    padding: "40px 0",
    fontSize: "14px",
  },
}));

export default function FullPageRewardCampaignsTable({
  allRewardCampaignData,
  allChamberMemberData,
  mode,
}: {
  allChamberMemberData: GetAllChamberMembersQuery | undefined;
  allRewardCampaignData: AllRewardCampaignDataType;
  mode: "active" | "inactive";
}) {
  const classes = useStyles();
  const history = useHistory();
  const isMobile = useMediaQuery("(max-width: 600px)");

  const enumStrings = useEnumStrings();

  const [mobileSearch, setMobileSearch] = React.useState("");
  const [mobileMenuAnchor, setMobileMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [mobileMenuCampaignId, setMobileMenuCampaignId] = React.useState<string | null>(null);

  const getInitials = (name: string) => {
    if (!name) return "?";
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "?";
    return words.slice(0, 3).map(w => w[0]).join("").toUpperCase();
  };
  const formatShortDate = (ms: number) => {
    if (!ms) return "-";
    try {
      return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return "-";
    }
  };

  // Column definitions for CommonTable
  const columns: HeadCell[] = React.useMemo(
    () => [
      {
        id: "companySponsor",
        label: enumStrings.rewardsPage.companySponsor,
        width: "200px",
      },
      {
        id: "campaignName",
        label: enumStrings.rewardsPage.campaignName,
      },
      {
        id: "prizeDescription",
        label: enumStrings.rewardsPage.prizeDescription,
      },
      {
        id: "startDateUtcMilli",
        label: enumStrings.rewardsPage.startDate,
        preventTextWrap: true,
        width: "140px",
      },
      {
        id: "endDateUtcMilli",
        label: enumStrings.rewardsPage.endDate,
        preventTextWrap: true,
        width: "140px",
      },
      {
        id: "prizeDrawParticipants",
        label: enumStrings.rewardsPage.usersEntered,
        sortable: false,
        alignCenter: true,
        width: "150px",
      },
      {
        id: "status",
        label: mode === "active"
          ? enumStrings.rewardsPage.contestStatus
          : enumStrings.rewardsPage.campaignStatus,
        alignCenter: true,
        width: "120px",
      },
    ],
    [enumStrings, mode]
  );

  // Transform data for table
  const allTableRewardCampaignData: AllTableRewardCampaignDataType =
    React.useMemo(() => {
      if (
        !allRewardCampaignData ||
        !allChamberMemberData?.getAllChamberMembers ||
        (allChamberMemberData?.getAllChamberMembers || []).length === 0
      ) {
        return [];
      }

      const allRewardCampaignsArr = (allRewardCampaignData || []).map(
        (rewardCampaignData) => ({
          ...rewardCampaignData,
          chamberMemberInfo: (
            allChamberMemberData?.getAllChamberMembers || []
          ).find(
            (chamberMember) =>
              chamberMember.id === rewardCampaignData.sponsoringChamberMemberId
          ),
        })
      ) as AllTableRewardCampaignDataType;

      if (mode === "active") {
        return allRewardCampaignsArr.filter((campaign) => {
          const { campaignIsActive, campaignIsUpcoming } =
            checkIfRewardCampaignIsActive({
              campaign,
            });

          return campaignIsActive || campaignIsUpcoming;
        });
      }

      return allRewardCampaignsArr.filter((campaign) => {
        const { campaignIsActive, campaignIsUpcoming } =
          checkIfRewardCampaignIsActive({
            campaign,
          });

        return !campaignIsActive && !campaignIsUpcoming;
      });
    }, [allChamberMemberData, allRewardCampaignData, mode]);

  // Modal state
  const [memberModalOpenedForCampaignsId, setMemberModalOpenedForCampaignsId] =
    React.useState<string>("");

  const [participantModalOpen, setParticipantModalOpen] =
    React.useState<boolean>(false);

  // Track if the opened modal is for an active campaign
  const [modalIsForActiveCampaign, setModalIsForActiveCampaign] =
    React.useState<boolean>(false);

  // Random winner state
  const [randomWinnerModalOpen, setRandomWinnerModalOpen] =
    React.useState<boolean>(false);
  const [randomWinner, setRandomWinner] =
    React.useState<PrizeDrawParticipantReturn | null>(null);

  const currentParticipants = React.useMemo(() => {
    if (
      memberModalOpenedForCampaignsId.length > 0 &&
      (allRewardCampaignData || []).length > 0
    ) {
      return (
        (allRewardCampaignData || []).find(
          (campaign) => campaign.id === memberModalOpenedForCampaignsId
        )?.prizeDrawParticipants || []
      );
    }
    return [];
  }, [allRewardCampaignData, memberModalOpenedForCampaignsId]);

  const [endCampaignModalOpen, setEndCampaignModalOpen] = React.useState(false);
  const [endModalCampaignId, setEndModalCampaignId] = React.useState("");

  // Pick random winner handler
  const handlePickRandomWinner = React.useCallback(() => {
    if (currentParticipants.length > 0) {
      const randomIndex = Math.floor(Math.random() * currentParticipants.length);
      setRandomWinner(currentParticipants[randomIndex]);
      setRandomWinnerModalOpen(true);
    }
  }, [currentParticipants]);

  // Download participant list as CSV
  const handleDownloadList = React.useCallback(() => {
    if (currentParticipants.length === 0) return;

    const csvContent = [
      [enumStrings.rewardsPage.participantEmail, enumStrings.rewardsPage.pointsEarned].join(","),
      ...currentParticipants.map((p) => [p.email, p.ballotsEarned].join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "prize_draw_participants.csv";
    link.click();
    URL.revokeObjectURL(url);
  }, [currentParticipants, enumStrings.rewardsPage.participantEmail, enumStrings.rewardsPage.pointsEarned]);

  // Custom cell renderer
  const renderCell = React.useCallback(
    (column: HeadCell, row: TableRewardCampaignDataType) => {
      const { campaignIsActive, campaignIsUpcoming } =
        checkIfRewardCampaignIsActive({
          campaign: row,
        });

      switch (column.id) {
        case "companySponsor":
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <LogoImage
                logoSource={row?.chamberMemberInfo?.logo || ""}
                size="large"
                squared
                showPointerCursor={mode === "active" && !row?.isCreatedByCpaAdmin}
                chamberName={row?.chamberMemberInfo?.name || "Deleted Member"}
              />
              <span style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 500, fontSize: "14px", lineHeight: "17px", color: "#000000" }}>
                {row?.chamberMemberInfo?.name || enumStrings.rewardsPage.deletedMember}
              </span>
            </div>
          );

        case "campaignName":
          return row.campaignName;

        case "prizeDescription":
          return row.prizeDescription;

        case "startDateUtcMilli":
          return (
            <span style={{ whiteSpace: "nowrap" }}>
              {getLocalTimestamp(row.startDateUtcMilli || 0, true)}
            </span>
          );

        case "endDateUtcMilli":
          return (
            <span style={{ whiteSpace: "nowrap" }}>
              {getLocalTimestamp(row.endDateUtcMilli || 0, true)}
            </span>
          );

        case "prizeDrawParticipants":
          return (
            <div style={{ textAlign: "center" }}>
              {row?.isCreatedByCpaAdmin ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    height: "100%",
                    flex: 1,
                  }}
                >
                  N/A
                  <div style={{ marginLeft: 7 }}>
                    <HoverToolTip
                      description={enumStrings.rewardsPage.adminCreatedCampaign.replace(
                        "{{supportEmail}}",
                        enumStrings.supportContactEmail
                      )}
                      showIt
                      disablePadding
                    />
                  </div>
                </div>
              ) : (
                <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "14px", lineHeight: "17px", marginBottom: "6px", textAlign: "center", color: "#000000" }}>
                  {(row?.prizeDrawParticipants || []).length}
                </div>
              )}

              {!row?.isCreatedByCpaAdmin && (
                <Button
                  className={mode === "active" ? classes.viewUsersButton : classes.viewUsersButtonInactive}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMemberModalOpenedForCampaignsId(row.id);
                    setModalIsForActiveCampaign(campaignIsActive || campaignIsUpcoming);
                    setParticipantModalOpen(true);
                  }}
                  variant="contained"
                >
                  {mode === "active"
                    ? enumStrings.rewardsPage.viewUsersPickWinner
                    : enumStrings.rewardsPage.viewUsers}
                </Button>
              )}
            </div>
          );

        case "status":
          return campaignIsActive || campaignIsUpcoming ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span
                style={{
                  color: Date.now() < row.startDateUtcMilli ? "#525100" : "#00B621",
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 700,
                  fontSize: "14px",
                  lineHeight: "17px",
                  textTransform: "uppercase",
                }}
              >
                {Date.now() < row.startDateUtcMilli
                  ? enumStrings.rewardsPage.upcoming
                  : enumStrings.rewardsPage.active}
              </span>
              {!row?.isCreatedByCpaAdmin && (
                <Button
                  className={classes.updateButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    history.push(RedirectUri.updateReward, {
                      rewardCampaignId: row.id,
                    });
                  }}
                  variant="contained"
                >
                  {enumStrings.rewardsPage.update}
                </Button>
              )}
            </div>
          ) : (
            <span
              style={{
                color: "#727272",
                fontWeight: "bold",
                fontSize: "14px",
                textTransform: "uppercase",
              }}
            >
              {enumStrings.rewardsPage.ended}
            </span>
          );

        default:
          return null;
      }
    },
    [classes.viewUsersButton, classes.viewUsersButtonInactive, classes.updateButton, enumStrings, history, mode]
  );

  // Handle row click for navigation
  const handleRowClick = React.useCallback(
    (row: TableRewardCampaignDataType) => {
      if (row?.isCreatedByCpaAdmin) {
        return;
      }

      if (mode === "active") {
        history.push(RedirectUri.updateReward, {
          rewardCampaignId: row.id,
        });
      }
    },
    [history, mode]
  );

  // Empty state message
  const emptyMessage =
    mode === "active"
      ? enumStrings.rewardsPage.noActiveRewards
      : enumStrings.rewardsPage.noPreviousRewards;

  return (
    <>
      {endCampaignModalOpen && (
        <EndCampaignModal
          campaignId={endModalCampaignId}
          closeModal={() => setEndCampaignModalOpen(false)}
        />
      )}

      {/* Participant Modal */}
      {participantModalOpen && (
        <Modal className={classes.modal} open={participantModalOpen}>
          <div className={classes.paper}>
            {/* Close button */}
            <IconButton
              className={classes.closeButton}
              onClick={() => setParticipantModalOpen(false)}
              aria-label={enumStrings.rewardsPage.close}
            >
              <CloseIcon style={{ fontSize: "18px" }} />
            </IconButton>

            {/* Checklist icon */}
            <div
              style={{
                width: "70px",
                height: "70px",
                borderRadius: "50%",
                backgroundColor: "#2C3E50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <FormatListBulletedIcon style={{ fontSize: "40px", color: "#FFFFFF" }} />
            </div>

            {/* Title */}
            <h2
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontWeight: 700,
                fontSize: "22px",
                color: "#000000",
                marginBottom: "12px",
                marginTop: 0,
              }}
            >
              {enumStrings.rewardsPage.prizeDrawParticipantsTitle}
            </h2>

            {/* Description */}
            <p
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontSize: "14px",
                color: "#000000",
                lineHeight: "1.6",
                marginBottom: "20px",
                maxWidth: "580px",
                textAlign: "center",
              }}
            >
              {enumStrings.rewardsPage.prizeDrawParticipantsDescription}
            </p>

            {/* Participant table - scrollable */}
            <div
              style={{
                width: "100%",
                maxWidth: "560px",
                flex: 1,
                overflowY: "auto",
                maxHeight: "300px",
                marginBottom: "24px",
              }}
            >
              {currentParticipants.length > 0 ? (
                <ParticipantTable data={currentParticipants} />
              ) : (
                <div style={{ padding: "20px", textAlign: "center" }}>
                  {enumStrings.rewardsPage.noneSoFar}
                </div>
              )}
            </div>

            {/* Action buttons - only for active campaigns */}
            {modalIsForActiveCampaign && currentParticipants.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  onClick={handlePickRandomWinner}
                  variant="outlined"
                  style={{
                    borderColor: "#E1A731",
                    color: "#E1A731",
                    fontFamily: "DM Sans, sans-serif",
                    fontWeight: 700,
                    fontSize: "14px",
                    borderRadius: "8px",
                    padding: "10px 24px",
                    textTransform: "uppercase",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <EmojiEventsIcon style={{ fontSize: "20px" }} />
                  {enumStrings.rewardsPage.pickRandomWinner}
                </Button>
                <Button
                  onClick={handleDownloadList}
                  variant="contained"
                  style={{
                    backgroundColor: "#2C3E50",
                    color: "#FFFFFF",
                    fontFamily: "DM Sans, sans-serif",
                    fontWeight: 700,
                    fontSize: "14px",
                    borderRadius: "8px",
                    padding: "10px 24px",
                    textTransform: "uppercase",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <GetAppIcon style={{ fontSize: "20px" }} />
                  {enumStrings.rewardsPage.downloadList}
                </Button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Random Winner Modal */}
      {randomWinnerModalOpen && randomWinner && (
        <Modal className={classes.modal} open={randomWinnerModalOpen}>
          <div className={classes.winnerPaper}>
            {/* Close button */}
            <IconButton
              className={classes.closeButton}
              onClick={() => {
                setRandomWinnerModalOpen(false);
                setRandomWinner(null);
              }}
              aria-label={enumStrings.rewardsPage.close}
            >
              <CloseIcon style={{ fontSize: "18px" }} />
            </IconButton>

            {/* Winner icon */}
            <div
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                backgroundColor: "#FFF3D6",
                border: "2px solid #E1A731",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
              }}
            >
              <EmojiEventsIcon style={{ fontSize: "45px", color: "#E1A731" }} />
            </div>

            {/* Title */}
            <h2
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontWeight: 700,
                fontSize: "24px",
                color: "#000000",
                marginBottom: "12px",
                marginTop: 0,
              }}
            >
              {enumStrings.rewardsPage.randomWinnerIs}
            </h2>

            {/* Winner email */}
            <p
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontSize: "18px",
                color: "#E1A731",
                fontWeight: 600,
                marginBottom: "12px",
              }}
            >
              {randomWinner.email}
            </p>

            {/* Redeemed perks text */}
            <p
              style={{
                fontFamily: "DM Sans, sans-serif",
                fontSize: "14px",
                color: "#000000",
                textAlign: "center",
                marginBottom: "10px",
              }}
            >
              {enumStrings.rewardsPage.redeemedPerksText.replace(
                "{{count}}",
                String(randomWinner.ballotsEarned)
              )}
            </p>
          </div>
        </Modal>
      )}

      {isMobile ? (() => {
        const q = mobileSearch.trim().toLowerCase();
        const filtered = q
          ? allTableRewardCampaignData.filter((row) => {
              const haystack = [
                row.chamberMemberInfo?.name || "",
                row.campaignName || "",
                row.prizeDescription || "",
              ].join(" ").toLowerCase();
              return haystack.includes(q);
            })
          : allTableRewardCampaignData;
        const menuCampaign = filtered.find((c) => c.id === mobileMenuCampaignId);
        return (
          <div className={classes.mobileList}>
            <TextField
              className={classes.mobileSearchBar}
              variant="outlined"
              size="small"
              placeholder={enumStrings.rewardsPage.searchPlaceholder || "Search Company Sponsor, Campaign Name or Prize"}
              value={mobileSearch}
              onChange={(e) => setMobileSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "#666", fontSize: "18px" }} />
                  </InputAdornment>
                ),
              }}
            />
            {filtered.length === 0 ? (
              <div className={classes.mobileEmpty}>{emptyMessage}</div>
            ) : (
              filtered.map((row) => {
                const { campaignIsActive, campaignIsUpcoming } =
                  checkIfRewardCampaignIsActive({ campaign: row });
                const isEnded = !(campaignIsActive || campaignIsUpcoming);
                const isUpcoming = !isEnded && Date.now() < row.startDateUtcMilli;
                const statusLabel = isEnded
                  ? enumStrings.rewardsPage.ended
                  : isUpcoming
                  ? enumStrings.rewardsPage.upcoming
                  : enumStrings.rewardsPage.active;
                const pillClass = isEnded
                  ? classes.mobileStatusPill
                  : `${classes.mobileStatusPill} ${isUpcoming ? classes.mobileStatusPillUpcoming : classes.mobileStatusPillActive}`;
                const sponsorName =
                  row?.chamberMemberInfo?.name || enumStrings.rewardsPage.deletedMember;
                const count = (row?.prizeDrawParticipants || []).length;
                const canUpdate = !row?.isCreatedByCpaAdmin && mode === "active";
                return (
                  <div key={row.id} className={classes.mobileCard}>
                    <div className={classes.mobileCardTop}>
                      <div className={classes.mobileAvatar}>{getInitials(sponsorName)}</div>
                      <div className={classes.mobileTitles}>
                        <div className={classes.mobileSponsorName}>{sponsorName}</div>
                        {row.campaignName ? (
                          <div className={classes.mobileCampaignName}>{row.campaignName}</div>
                        ) : null}
                      </div>
                      <span className={pillClass}>{statusLabel}</span>
                      {canUpdate && (
                        <IconButton
                          size="small"
                          className={classes.mobileMenuBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            setMobileMenuAnchor(e.currentTarget);
                            setMobileMenuCampaignId(row.id);
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                    <div className={classes.mobileDivider} />
                    <div className={classes.mobileMetaRow}>
                      <span className={classes.mobileMetaLabel}>{enumStrings.rewardsPage.prizeDescription}</span>
                      <span className={classes.mobileMetaValue}>{row.prizeDescription || "-"}</span>
                    </div>
                    <div className={classes.mobileMetaRow}>
                      <span className={classes.mobileMetaLabel}>{enumStrings.rewardsPage.startDate}</span>
                      <span className={classes.mobileMetaValue}>{formatShortDate(row.startDateUtcMilli || 0)}</span>
                    </div>
                    <div className={classes.mobileMetaRow}>
                      <span className={classes.mobileMetaLabel}>{enumStrings.rewardsPage.endDate}</span>
                      <span className={classes.mobileMetaValue}>{formatShortDate(row.endDateUtcMilli || 0)}</span>
                    </div>
                    <div className={classes.mobileDivider} />
                    <div className={classes.mobileBottomRow}>
                      <div>
                        <div className={classes.mobileCountNumber}>
                          {row?.isCreatedByCpaAdmin ? "N/A" : count}
                        </div>
                        <div className={classes.mobileCountLabel}>
                          {enumStrings.rewardsPage.participants || "Users entered"}
                        </div>
                      </div>
                      {!row?.isCreatedByCpaAdmin && (
                        <Button
                          className={classes.mobileViewUsersBtn}
                          onClick={() => {
                            setMemberModalOpenedForCampaignsId(row.id);
                            setModalIsForActiveCampaign(campaignIsActive || campaignIsUpcoming);
                            setParticipantModalOpen(true);
                          }}
                        >
                          {mode === "active"
                            ? enumStrings.rewardsPage.viewUsersPickWinner
                            : enumStrings.rewardsPage.viewUsers}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor) && !!menuCampaign}
              onClose={() => {
                setMobileMenuAnchor(null);
                setMobileMenuCampaignId(null);
              }}
              PaperProps={{ style: { borderRadius: "10px", minWidth: "160px" } }}
            >
              <MenuItem
                onClick={() => {
                  const id = mobileMenuCampaignId;
                  setMobileMenuAnchor(null);
                  setMobileMenuCampaignId(null);
                  if (id) {
                    history.push(RedirectUri.updateReward, { rewardCampaignId: id });
                  }
                }}
                style={{ fontSize: "14px", padding: "10px 16px" }}
              >
                {enumStrings.rewardsPage.update}
              </MenuItem>
            </Menu>
          </div>
        );
      })() : (
      <CommonTable<TableRewardCampaignDataType>
        data={allTableRewardCampaignData}
        columns={columns}
        defaultSortColumn="startDateUtcMilli"
        defaultSortOrder="desc"
        rowKeyField="id"
        renderCell={renderCell}
        onRowClick={mode === "active" ? handleRowClick : undefined}
        emptyMessage={emptyMessage}
        searchPlaceholder={enumStrings.rewardsPage.searchPlaceholder || "Search Company Sponsor, Campaign Name or Prize"}
        searchFields={["name", "campaignName", "prizeDescription"]}
      />
      )}
    </>
  );
}
